import React, { FC, useState, useEffect, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Line } from 'react-chartjs-2';
import {
  OverviewMessagesResponse,
  OverviewUsersResponse,
  OverviewCostsResponse,
} from '@dreamai/types';
import api from '../../api';

/**
 * Overview tab of Platform Stats: three independent line charts (message
 * volume by channel, activated vs not-activated users, LLM cost), each with
 * its own timeframe selector.
 *
 * "Activated" = a qualified user who has sent 5+ messages, ever. The message
 * chart only counts messages from activated senders.
 */

const TIMEFRAMES = [7, 14, 30, 60, 90];

const formatLabel = (iso: string) => {
  const d = new Date(iso);
  return `${d.getUTCDate()}/${d.getUTCMonth() + 1}`;
};

interface ChartCardProps {
  title: string;
  subtitle?: string;
  days: number;
  onDaysChange: (d: number) => void;
  loading: boolean;
  children: React.ReactNode;
}

const ChartCard: FC<ChartCardProps> = ({
  title,
  subtitle,
  days,
  onDaysChange,
  loading,
  children,
}) => (
  <div className='chart-card wide overview-chart-card'>
    <div className='overview-chart-header'>
      <div>
        <h3>{title}</h3>
        {subtitle && <p className='overview-chart-subtitle'>{subtitle}</p>}
      </div>
      <div className='day-selector'>
        {TIMEFRAMES.map((d) => (
          <button
            key={d}
            className={`day-btn ${days === d ? 'active' : ''}`}
            onClick={() => onDaysChange(d)}
          >
            {d}d
          </button>
        ))}
      </div>
    </div>
    {loading ? <div className='loading'>Loading...</div> : children}
  </div>
);

const gridColor = 'rgba(148, 163, 184, 0.15)';

const stackedLineOpts = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index' as const, intersect: false },
  plugins: {
    legend: {
      position: 'bottom' as const,
      labels: { usePointStyle: true, boxWidth: 8, boxHeight: 8 },
    },
  },
  scales: {
    y: { stacked: true, beginAtZero: true, grid: { color: gridColor } },
    x: { stacked: true, grid: { display: false } },
  },
};

const lineOpts = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index' as const, intersect: false },
  plugins: { legend: { display: false } },
  scales: {
    y: { beginAtZero: true, grid: { color: gridColor } },
    x: { grid: { display: false } },
  },
};

const OverviewDashboard: FC = () => {
  const { getAccessTokenSilently } = useAuth0();

  const [messagesDays, setMessagesDays] = useState(30);
  const [usersDays, setUsersDays] = useState(30);
  const [costsDays, setCostsDays] = useState(30);

  const [messages, setMessages] = useState<OverviewMessagesResponse | null>(
    null,
  );
  const [users, setUsers] = useState<OverviewUsersResponse | null>(null);
  const [costs, setCosts] = useState<OverviewCostsResponse | null>(null);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(true);
  const [costsLoading, setCostsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSeries = useCallback(
    async <T,>(
      path: string,
      days: number,
      setData: (d: T) => void,
      setLoading: (b: boolean) => void,
    ) => {
      setLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const res = await api.get<T>(
          `/platform-stats/overview/${path}?days=${days}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setData(res.data);
      } catch {
        setError('Failed to load overview data');
      } finally {
        setLoading(false);
      }
    },
    [getAccessTokenSilently],
  );

  useEffect(() => {
    fetchSeries<OverviewMessagesResponse>(
      'messages',
      messagesDays,
      setMessages,
      setMessagesLoading,
    );
  }, [fetchSeries, messagesDays]);

  useEffect(() => {
    fetchSeries<OverviewUsersResponse>(
      'users',
      usersDays,
      setUsers,
      setUsersLoading,
    );
  }, [fetchSeries, usersDays]);

  useEffect(() => {
    fetchSeries<OverviewCostsResponse>(
      'costs',
      costsDays,
      setCosts,
      setCostsLoading,
    );
  }, [fetchSeries, costsDays]);

  return (
    <div className='charts-grid overview-dashboard'>
      {error && <div className='msg error'>{error}</div>}

      <ChartCard
        title='Messages Sent'
        subtitle='WhatsApp vs In-App — activated senders only (5+ lifetime messages)'
        days={messagesDays}
        onDaysChange={setMessagesDays}
        loading={messagesLoading}
      >
        <div className='overview-chart-body'>
          <Line
            options={stackedLineOpts}
            data={{
              labels: messages?.days.map(formatLabel) ?? [],
              datasets: [
                {
                  label: 'WhatsApp',
                  data: messages?.whatsapp ?? [],
                  borderColor: 'rgb(34, 197, 94)',
                  backgroundColor: 'rgba(34, 197, 94, 0.2)',
                  fill: true,
                  tension: 0.4,
                  pointRadius: 0,
                  pointHoverRadius: 4,
                  borderWidth: 2,
                },
                {
                  label: 'In-App',
                  data: messages?.inApp ?? [],
                  borderColor: 'rgb(59, 130, 246)',
                  backgroundColor: 'rgba(59, 130, 246, 0.2)',
                  fill: true,
                  tension: 0.4,
                  pointRadius: 0,
                  pointHoverRadius: 4,
                  borderWidth: 2,
                },
              ],
            }}
          />
        </div>
      </ChartCard>

      <ChartCard
        title='Users'
        subtitle='Activated (5+ messages ever) vs not yet activated'
        days={usersDays}
        onDaysChange={setUsersDays}
        loading={usersLoading}
      >
        <div className='overview-chart-body'>
          <Line
            options={stackedLineOpts}
            data={{
              labels: users?.days.map(formatLabel) ?? [],
              datasets: [
                {
                  label: 'Activated',
                  data: users?.activated ?? [],
                  borderColor: 'rgb(139, 92, 246)',
                  backgroundColor: 'rgba(139, 92, 246, 0.2)',
                  fill: true,
                  tension: 0.4,
                  pointRadius: 0,
                  pointHoverRadius: 4,
                  borderWidth: 2,
                },
                {
                  label: 'Not activated',
                  data: users?.notActivated ?? [],
                  borderColor: 'rgb(148, 163, 184)',
                  backgroundColor: 'rgba(148, 163, 184, 0.2)',
                  fill: true,
                  tension: 0.4,
                  pointRadius: 0,
                  pointHoverRadius: 4,
                  borderWidth: 2,
                },
              ],
            }}
          />
        </div>
      </ChartCard>

      <ChartCard
        title='Costs'
        subtitle='Total LLM cost per day (USD)'
        days={costsDays}
        onDaysChange={setCostsDays}
        loading={costsLoading}
      >
        <div className='overview-chart-body'>
          <Line
            options={lineOpts}
            data={{
              labels: costs?.days.map(formatLabel) ?? [],
              datasets: [
                {
                  label: 'Cost (USD)',
                  data: costs?.totalCost ?? [],
                  borderColor: 'rgb(239, 68, 68)',
                  backgroundColor: 'rgba(239, 68, 68, 0.08)',
                  fill: true,
                  tension: 0.4,
                  pointRadius: 0,
                  pointHoverRadius: 4,
                  borderWidth: 2,
                },
              ],
            }}
          />
        </div>
      </ChartCard>
    </div>
  );
};

export default OverviewDashboard;
